import axios, { AxiosInstance, AxiosResponse } from 'axios';

interface ImpersonatedCompanyInfo {
  cnpj: string;
  razaoSocial?: string;
}

interface SwitchCompanyResponse {
  accessToken: string;
  impersonatedCnpj?: string;
}

const ACCESS_TOKEN_KEY = 'accessToken';
const USER_KEY = 'user';
const USER_ROLE_KEY = 'userRole';
const SESSION_PUBLIC_KEY_KEY = 'sessionPublicKey';
const IMPERSONATED_COMPANY_KEY = 'impersonatedCompany';

export interface LoginResponse {
  accessToken: string;
  user: {
    id: string;
    name: string;
    email: string;
    role: 'DEVELOPER' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
    cnpj: string;
  };
  publicKey: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: 'DEVELOPER' | 'ADMIN' | 'MANAGER' | 'OPERATOR' | 'VIEWER';
  cnpj: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

class AuthService {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: process.env.REACT_APP_API_URL || 'http://localhost:3000/api',
      timeout: 30000,
    });

    // Interceptor para adicionar token JWT
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('accessToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // Interceptor para tratar erros 401
    this.client.interceptors.response.use(
      (response) => response,
      (error) => {
        if (error.response?.status === 401) {
          this.logout();
        }
        return Promise.reject(error);
      }
    );
  }

  // Métodos de autenticação
  async login(email: string, password: string, cnpj?: string): Promise<LoginResponse> {
    try {
      const response: AxiosResponse<LoginResponse> = await this.client.post('/auth/login', {
        email,
        password,
        cnpj,
      });

      // Armazenar token, dados do usuário e chave pública da sessão
      this.setAccessToken(response.data.accessToken);
      localStorage.setItem(USER_KEY, JSON.stringify(response.data.user));
      localStorage.setItem(USER_ROLE_KEY, response.data.user.role);
      this.clearImpersonatedCompany();
      if (response.data.publicKey) {
        localStorage.setItem(SESSION_PUBLIC_KEY_KEY, response.data.publicKey);
      }

      return response.data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao fazer login');
    }
  }

  async forgotPassword(email: string, cnpj?: string): Promise<void> {
    try {
      await this.client.post('/auth/forgot-password', { email, cnpj });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao solicitar recuperação de senha');
    }
  }

  async resetPassword(token: string, newPassword: string): Promise<void> {
    try {
      await this.client.post('/auth/reset-password', { token, newPassword });
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao redefinir senha');
    }
  }

  async switchCompany(params: { cnpj?: string | null; companyName?: string }): Promise<SwitchCompanyResponse> {
    try {
      const response: AxiosResponse<SwitchCompanyResponse> = await this.client.post('/auth/switch-company', {
        cnpj: params.cnpj ?? null,
      });

      const data = response.data;
      this.setAccessToken(data.accessToken);

      if (data.impersonatedCnpj) {
        this.setImpersonatedCompany({
          cnpj: data.impersonatedCnpj,
          razaoSocial: params.companyName,
        });
      } else {
        this.clearImpersonatedCompany();
      }

      return data;
    } catch (error: any) {
      throw new Error(error.response?.data?.error || 'Erro ao trocar de empresa');
    }
  }

  // Métodos utilitários
  logout(): void {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(USER_KEY);
    localStorage.removeItem(USER_ROLE_KEY);
    localStorage.removeItem(SESSION_PUBLIC_KEY_KEY);
    this.clearImpersonatedCompany();
    window.location.href = '/login';
  }

  isAuthenticated(): boolean {
    return !!localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  getUser(): User | null {
    const userStr = localStorage.getItem(USER_KEY);
    return userStr ? JSON.parse(userStr) : null;
  }

  getUserRole(): string | null {
    return localStorage.getItem(USER_ROLE_KEY);
  }

  getToken(): string | null {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  }

  setAccessToken(token: string): void {
    localStorage.setItem(ACCESS_TOKEN_KEY, token);
  }

  setImpersonatedCompany(company: ImpersonatedCompanyInfo): void {
    localStorage.setItem(IMPERSONATED_COMPANY_KEY, JSON.stringify(company));
  }

  clearImpersonatedCompany(): void {
    localStorage.removeItem(IMPERSONATED_COMPANY_KEY);
  }

  getImpersonatedCompany(): ImpersonatedCompanyInfo | null {
    const raw = localStorage.getItem(IMPERSONATED_COMPANY_KEY);
    if (!raw) {
      return null;
    }

    try {
      return JSON.parse(raw) as ImpersonatedCompanyInfo;
    } catch (error) {
      console.error('[authService] Falha ao ler impersonatedCompany do storage', error);
      this.clearImpersonatedCompany();
      return null;
    }
  }

  getActiveCompanyCnpj(): string | null {
    const impersonated = this.getImpersonatedCompany();
    if (impersonated?.cnpj) {
      return impersonated.cnpj;
    }

    return this.getUser()?.cnpj ?? null;
  }
}

export const authService = new AuthService();
export default authService;
